/**
 * Floor planner — the selected room's tools: a small floating bar of icons
 * beside the room (Space, Size, Ceiling, Finish, and Delete), each opening the
 * room editor (RoomEditDialog) on that part.
 *
 * It sits clear of the room, its handles and its dimension lines: upright at
 * the room's right, else its left; lying flat below it, else above; and when
 * the room fills the sheet, upright at the sheet's right edge, over the room.
 * Always inside the sheet.
 */

import React from 'react';
import { Trash2 } from 'lucide-react';
import { ROOM_STEP_ICON, RoomStep } from './roomSteps';
import { DESIGNER_STRINGS as S } from '../strings';
import './roomTools.css';

const T = S.planner.tools;

/** The bar's buttons and padding (roomTools.css): what its placement allows for. */
const BTN_W = 52;
const BTN_H = 50;
const GAP = 2;
const SEP = 9;
const PAD = 6;
/** Clear of the sheet's edges, and of the room. */
const EDGE = 8;
const CLEAR = 14;

export interface ScreenRect { x: number; y: number; w: number; h: number }

export interface RoomToolbarProps {
  /** The room (or the selection's bounds) on screen, inside the sheet. */
  room: ScreenRect;
  /** How far the wall band reaches outside the room, px. */
  wall: number;
  sheet: { w: number; h: number };
  /** Room at the sheet's top and bottom to keep free (the Design in 3D button). */
  keepTop: number;
  keepBottom: number;
  steps: RoomStep[];
  onPick: (step: RoomStep) => void;
  onDelete: () => void;
  deleteLabel: string;
  /** A drag in progress: the bar follows, faded, out of the way of the pointer. */
  dragging: boolean;
}

type Placement = { x: number; y: number; flat: boolean };

/** Where the bar goes: beside the room, else above or below it, else at the sheet's right edge. */
function place(room: ScreenRect, wall: number, sheet: { w: number; h: number }, count: number, keepTop: number, keepBottom: number): Placement {
  const long = count * BTN_H + count * GAP + SEP + 2 * PAD;
  const short = BTN_W + 2 * PAD;
  const top = EDGE + keepTop;
  const bottom = sheet.h - EDGE - keepBottom;
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
  const midY = clamp(room.y + room.h / 2 - long / 2, top, Math.max(top, bottom - long));
  const flatLong = count * BTN_W + count * GAP + SEP + 2 * PAD;
  const midX = clamp(room.x + room.w / 2 - flatLong / 2, EDGE, Math.max(EDGE, sheet.w - EDGE - flatLong));
  const fitsUpright = long <= bottom - top;

  // Right of the room: past the wall band and the handles.
  const right = room.x + room.w + wall + CLEAR;
  if (fitsUpright && right + short <= sheet.w - EDGE) return { x: right, y: midY, flat: false };
  // Left of it: past the wall band and the width dimension line with its figures.
  const left = room.x - wall - 16 - 22 - short;
  if (fitsUpright && left >= EDGE) return { x: left, y: midY, flat: false };
  // Below it.
  const below = room.y + room.h + wall + CLEAR;
  if (below + short <= bottom) return { x: midX, y: below, flat: true };
  // Above it: past the length dimension line.
  const above = room.y - wall - 16 - 22 - short;
  if (above >= top) return { x: midX, y: above, flat: true };
  // The room fills the sheet: at its right edge.
  return { x: sheet.w - EDGE - short, y: midY, flat: false };
}

export const RoomToolbar: React.FC<RoomToolbarProps> = ({ room, wall, sheet, keepTop, keepBottom, steps, onPick, onDelete, deleteLabel, dragging }) => {
  const at = place(room, wall, sheet, steps.length + 1, keepTop, keepBottom);
  return (
    <div
      className={`rt-bar${at.flat ? ' is-flat' : ''}${dragging ? ' is-dragging' : ''}`}
      role="toolbar" aria-label={T.label} aria-orientation={at.flat ? 'horizontal' : 'vertical'}
      style={{ left: at.x, top: at.y }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {steps.map((s) => {
        const Icon = ROOM_STEP_ICON[s];
        return (
          <button key={s} type="button" className="rt-btn" title={T.titles[s]} aria-label={T.titles[s]} onClick={() => onPick(s)}>
            <Icon size={18} />
            <span>{S.planner.wizard.steps[s]}</span>
          </button>
        );
      })}
      <span className="rt-sep" aria-hidden="true" />
      <button type="button" className="rt-btn is-danger" title={deleteLabel} aria-label={deleteLabel} onClick={onDelete}>
        <Trash2 size={18} />
      </button>
    </div>
  );
};

export default RoomToolbar;
